import React from 'react';
import { Link } from 'react-router-dom';

const FocusCoaching = () => {
  const coachingTypes = [
    {
      title: 'Coaching Individual',
      description: 'Desenvolvimento pessoal e profissional através de sessões individualizadas.',
      icon: '👤',
      features: [
        'Sessões personalizadas',
        'Foco em objetivos específicos',
        'Desenvolvimento de competências',
        'Acompanhamento contínuo'
      ],
      duration: '3-6 meses',
      sessions: '8-12 sessões'
    },
    {
      title: 'Coaching em Grupo',
      description: 'Desenvolvimento coletivo com foco em dinâmicas de equipe e colaboração.',
      icon: '👥',
      features: [
        'Dinâmicas de grupo',
        'Troca de experiências',
        'Desenvolvimento de liderança',
        'Fortalecimento de equipes'
      ],
      duration: '2-4 meses',
      sessions: '6-10 sessões'
    },
    {
      title: 'Coaching Executivo',
      description: 'Desenvolvimento de líderes e executivos para alta performance.',
      icon: '💼',
      features: [
        'Liderança estratégica',
        'Tomada de decisão',
        'Gestão de pessoas',
        'Visão de negócios'
      ],
      duration: '6-12 meses',
      sessions: '12-20 sessões'
    },
    {
      title: 'Coaching de Carreira',
      description: 'Orientação para transições e desenvolvimento profissional.',
      icon: '🚀',
      features: [
        'Planejamento de carreira',
        'Transições profissionais',
        'Desenvolvimento de competências',
        'Networking estratégico'
      ],
      duration: '3-6 meses',
      sessions: '8-15 sessões'
    }
  ];

  const benefits = [
    {
      icon: '🎯',
      title: 'Clareza de Objetivos',
      description: 'Definição clara de metas pessoais e profissionais'
    },
    {
      icon: '💪',
      title: 'Autoconfiança',
      description: 'Desenvolvimento da autoestima e confiança'
    },
    {
      icon: '🧠',
      title: 'Autoconhecimento',
      description: 'Maior consciência sobre potenciais e limitações'
    },
    {
      icon: '⚡',
      title: 'Performance',
      description: 'Melhoria significativa no desempenho profissional'
    },
    {
      icon: '🤝',
      title: 'Relacionamentos',
      description: 'Melhoria nas relações interpessoais'
    },
    {
      icon: '🎨',
      title: 'Criatividade',
      description: 'Desenvolvimento do pensamento criativo e inovador'
    }
  ];

  const testimonials = [
    {
      name: 'Carlos Mendes',
      position: 'Diretor Comercial',
      company: 'TechCorp',
      text: 'O coaching me ajudou a desenvolver habilidades de liderança que transformaram minha carreira.',
      image: '👨‍💼'
    },
    {
      name: 'Ana Paula',
      position: 'Gerente de Projetos',
      company: 'InnovaTech',
      text: 'Através do coaching, consegui clareza sobre meus objetivos e alcancei a promoção que tanto desejava.',
      image: '👩‍💼'
    },
    {
      name: 'Roberto Silva',
      position: 'Empresário',
      company: 'Silva & Associados',
      text: 'O coaching executivo me deu as ferramentas necessárias para liderar minha empresa com mais eficiência.',
      image: '👨‍💻'
    }
  ];

  return (
    <div className="pt-32"> {/* Espaço para header fixo */}
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-focus-blue to-blue-600 text-white py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Focus Coaching
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-blue-100">
              Desperte seu potencial máximo. Transforme sua vida pessoal e profissional através do coaching.
            </p>
            <Link
              to="/#contato"
              className="bg-focus-orange hover:bg-red-600 text-white px-8 py-4 rounded-lg text-lg font-semibold transition-colors inline-block"
            >
              Agende sua Sessão
            </Link>
          </div>
        </div>
      </section>

      {/* Sobre o Coaching */}
      <section className="py-20 bg-focus-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-focus-blue mb-6">
                O que é Coaching?
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                O coaching é um processo de desenvolvimento humano que utiliza técnicas e ferramentas 
                específicas para ajudar pessoas a alcançarem seus objetivos pessoais e profissionais.
              </p>
              <p className="text-lg text-gray-600 mb-6">
                Através de conversas estruturadas e exercícios práticos, o coach ajuda o coachee a 
                descobrir seu potencial, superar limitações e criar um plano de ação efetivo.
              </p>
              <p className="text-lg text-gray-600 mb-8">
                Nossos coaches são certificados internacionalmente e possuem vasta experiência em 
                desenvolvimento humano e organizacional.
              </p>
              <div className="grid grid-cols-3 gap-4 text-center">
                <div className="bg-white p-4 rounded-lg shadow">
                  <div className="text-2xl font-bold text-focus-blue">200+</div>
                  <div className="text-sm text-gray-600">Clientes Atendidos</div>
                </div>
                <div className="bg-white p-4 rounded-lg shadow">
                  <div className="text-2xl font-bold text-focus-blue">95%</div>
                  <div className="text-sm text-gray-600">Taxa de Sucesso</div>
                </div>
                <div className="bg-white p-4 rounded-lg shadow">
                  <div className="text-2xl font-bold text-focus-blue">5+</div>
                  <div className="text-sm text-gray-600">Anos de Experiência</div>
                </div>
              </div>
            </div>
            <div className="bg-focus-pink rounded-lg p-8">
              <h3 className="text-2xl font-bold text-focus-blue mb-6">Por que escolher nosso Coaching?</h3>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <svg className="w-6 h-6 text-green-500 mr-3 mt-1" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  <span className="text-gray-700">Coaches certificados internacionalmente</span>
                </li>
                <li className="flex items-start">
                  <svg className="w-6 h-6 text-green-500 mr-3 mt-1" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  <span className="text-gray-700">Metodologia comprovada e personalizada</span>
                </li>
                <li className="flex items-start">
                  <svg className="w-6 h-6 text-green-500 mr-3 mt-1" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  <span className="text-gray-700">Acompanhamento contínuo e suporte</span>
                </li>
                <li className="flex items-start">
                  <svg className="w-6 h-6 text-green-500 mr-3 mt-1" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  <span className="text-gray-700">Resultados mensuráveis e duradouros</span>
                </li>
                <li className="flex items-start">
                  <svg className="w-6 h-6 text-green-500 mr-3 mt-1" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  <span className="text-gray-700">Flexibilidade de horários e formatos</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Tipos de Coaching */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-focus-blue mb-4">
              Nossos Tipos de Coaching
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Oferecemos diferentes modalidades de coaching para atender suas necessidades específicas
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {coachingTypes.map((type, index) => (
              <div
                key={index}
                className="bg-focus-white rounded-lg shadow-lg p-8 hover:shadow-xl transition-shadow border border-gray-100"
              >
                <div className="flex items-center mb-6">
                  <div className="bg-focus-blue w-16 h-16 rounded-full flex items-center justify-center text-2xl mr-4">
                    {type.icon}
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-focus-blue mb-1">
                      {type.title}
                    </h3>
                    <p className="text-gray-600 text-sm">
                      {type.description}
                    </p>
                  </div>
                </div>
                
                <div className="mb-6">
                  <h4 className="font-semibold text-focus-blue mb-3">Características:</h4>
                  <ul className="space-y-2">
                    {type.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center text-sm text-gray-600">
                        <svg className="w-4 h-4 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
                
                <div className="border-t border-gray-200 pt-4 flex justify-between text-sm text-gray-600">
                  <span><strong>Duração:</strong> {type.duration}</span>
                  <span><strong>Sessões:</strong> {type.sessions}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefícios */}
      <section className="py-20 bg-focus-pink">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-focus-blue mb-4">
              Benefícios do Coaching
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Descubra como o coaching pode transformar sua vida pessoal e profissional
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <div
                key={index}
                className="bg-white rounded-lg shadow-lg p-6 text-center hover:shadow-xl transition-shadow"
              >
                <div className="text-4xl mb-4">{benefit.icon}</div>
                <h3 className="text-lg font-semibold text-focus-blue mb-2">
                  {benefit.title}
                </h3>
                <p className="text-gray-600 text-sm">
                  {benefit.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Depoimentos */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-focus-blue mb-4">
              Depoimentos de Sucesso
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Veja como o coaching transformou a vida de nossos clientes
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div
                key={index}
                className="bg-focus-white rounded-lg shadow-lg p-6 hover:shadow-xl transition-shadow"
              >
                <div className="text-center mb-4">
                  <div className="text-6xl mb-2">{testimonial.image}</div>
                  <h4 className="font-semibold text-focus-blue">{testimonial.name}</h4>
                  <p className="text-sm text-gray-600">{testimonial.position}</p>
                  <p className="text-xs text-gray-500">{testimonial.company}</p>
                </div>
                <p className="text-gray-600 italic text-center">
                  "{testimonial.text}"
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-focus-orange text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Pronto para Transformar sua Vida?
          </h2>
          <p className="text-xl mb-8 text-red-100 max-w-2xl mx-auto">
            Agende uma sessão de coaching e comece sua jornada de transformação hoje mesmo!
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="tel:+5511123456789"
              className="bg-white text-focus-orange hover:bg-gray-100 px-8 py-4 rounded-lg text-lg font-semibold transition-colors inline-block"
            >
              📞 Agende sua Sessão
            </a>
            <a
              href="mailto:contato@focus.com.br"
              className="bg-transparent border-2 border-white hover:bg-white hover:text-focus-orange text-white px-8 py-4 rounded-lg text-lg font-semibold transition-colors inline-block"
            >
              ✉️ Saiba Mais
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FocusCoaching;